/* ============================================================
   The Cabretta PDP — Pack Picker
   Packs are separate products (Single = The Cabretta, 3-pack and
   6-pack = Shopify fixed bundles built from it), so bundle
   inventory stays synced to the single-glove stock.
   Switching packs swaps the variant in the add-to-cart form
   in place; Hand + Size carry over.
   ============================================================ */

class FundayPackPicker extends HTMLElement {
  connectedCallback() {
    const packsEl = this.querySelector('[data-funday-packs]');
    if (!packsEl) return;

    try {
      this.packs = JSON.parse(packsEl.textContent) || [];
    } catch (e) {
      console.error('FundayPackPicker: bad pack data', e);
      return;
    }
    if (!this.packs.length) return;

    this.productId = String(this.dataset.productId || '');
    this.mostPopular = this.dataset.mostPopular || '';

    this.packs.forEach((pack) => {
      pack.count = this.glovesInPack(pack.label);
    });

    const initial = this.getInitialSelection();
    this.packIndex = initial.packIndex;
    this.selected = initial.options;

    this.form = this.querySelector('form');
    this.setupSubscriptionGuard();

    this.bindEvents();
    this.update();
  }

  /* Bundles can't be bought on a subscription (selling plan). The
     subscription widget (Seal) injects into this form for the single,
     so hide it and strip any selling plan while a multi-pack is chosen. */
  setupSubscriptionGuard() {
    if (!document.getElementById('funday-pack-picker-style')) {
      const style = document.createElement('style');
      style.id = 'funday-pack-picker-style';
      style.textContent =
        '.funday-pack-multi .sealsubs-target-element,' +
        '.funday-pack-multi .sealsubs-container,' +
        '.funday-pack-multi [data-selling-plan-widget]{display:none !important;}';
      document.head.appendChild(style);
    }
    if (!this.form) return;

    const isMulti = () => this.packs[this.packIndex].count > 1;
    this.form.addEventListener('formdata', (e) => {
      if (isMulti()) e.formData.delete('selling_plan');
    });
    this.form.addEventListener('submit', () => {
      if (isMulti()) this.clearSellingPlan();
    }, true);
  }

  clearSellingPlan() {
    if (!this.form) return;
    this.form.querySelectorAll('[name="selling_plan"]').forEach((el) => {
      if (el.type === 'radio' || el.type === 'checkbox') el.checked = false;
      else el.value = '';
    });
    const oneTime = this.form.querySelector('input[type="radio"][value="one_time"]');
    if (oneTime) oneTime.checked = true;
  }

  glovesInPack(label) {
    if (!label) return 1;
    if (/single/i.test(label)) return 1;
    const match = String(label).match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  }

  firstAvailable(pack) {
    return pack.variants.find((v) => v.available) || pack.variants[0];
  }

  getInitialSelection() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');

    // 1. ?variant= from any pack (links keep working across packs)
    if (variantId) {
      for (let i = 0; i < this.packs.length; i++) {
        const v = this.packs[i].variants.find((x) => String(x.id) === String(variantId));
        if (v) return { packIndex: i, options: v.options.slice() };
      }
    }

    // 2. On a pack product's own page (not the single), preselect that pack
    const ownIndex = this.packs.findIndex((p) => String(p.productId) === this.productId);
    let packIndex = -1;
    if (ownIndex > 0) packIndex = ownIndex;

    // 3. Otherwise the "most popular" pack, then the current product, then first
    if (packIndex < 0 && this.mostPopular) {
      packIndex = this.packs.findIndex((p) => p.label === this.mostPopular);
    }
    if (packIndex < 0) packIndex = ownIndex >= 0 ? ownIndex : 0;

    const v = this.firstAvailable(this.packs[packIndex]);
    return { packIndex, options: v ? v.options.slice() : [] };
  }

  bindEvents() {
    this.querySelectorAll('[data-funday-pack-index]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.packIndex = parseInt(btn.dataset.fundayPackIndex, 10);
        this.update();
      });
    });

    this.querySelectorAll('[data-funday-option-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const index = parseInt(btn.dataset.fundayOptionIndex, 10);
        this.selected[index] = btn.dataset.fundayOptionValue;
        this.update();
      });
    });
  }

  matches(variant, overrideIndex, overrideValue) {
    return this.selected.every((val, i) => {
      const want = i === overrideIndex ? overrideValue : val;
      return want == null || variant.options[i] === want;
    });
  }

  findVariant(packIndex) {
    const pack = this.packs[packIndex];
    return pack ? pack.variants.find((v) => this.matches(v)) : null;
  }

  formatMoney(cents) {
    const dollars = cents / 100;
    const hasCents = dollars % 1 !== 0;
    return '$' + dollars.toFixed(hasCents ? 2 : 0);
  }

  updatePackRows() {
    // Baseline for savings: the single's price for the current hand/size
    const singleIndex = this.packs.findIndex((p) => p.count === 1);
    let singlePrice = null;
    if (singleIndex >= 0) {
      const sv = this.findVariant(singleIndex) || this.firstAvailable(this.packs[singleIndex]);
      if (sv) singlePrice = sv.price;
    }

    this.packs.forEach((pack, i) => {
      const row = this.querySelector(`[data-funday-pack-index="${i}"]`);
      if (!row) return;

      const exact = this.findVariant(i);
      const variant = exact || this.firstAvailable(pack);
      row.classList.toggle('is-selected', i === this.packIndex);
      row.classList.toggle('is-unavailable', !exact || !exact.available);
      row.setAttribute('aria-pressed', i === this.packIndex ? 'true' : 'false');
      if (!variant) return;

      const priceEl = row.querySelector('[data-funday-pack-price]');
      const subEl = row.querySelector('[data-funday-pack-sub]');
      const perGlove = Math.round(variant.price / pack.count);
      const savings = singlePrice ? singlePrice * pack.count - variant.price : 0;

      if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
      if (subEl) {
        let text = `${this.formatMoney(perGlove)} / glove`;
        if (savings > 0 && pack.count > 1) text += ` · save ${this.formatMoney(savings)}`;
        if (exact && !exact.available) text = 'Sold out in this size';
        subEl.textContent = text;
      }
    });
  }

  updateChips() {
    const pack = this.packs[this.packIndex];
    this.querySelectorAll('[data-funday-option-value]').forEach((btn) => {
      const index = parseInt(btn.dataset.fundayOptionIndex, 10);
      const value = btn.dataset.fundayOptionValue;
      const isSelected = this.selected[index] === value;
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      const anyAvailable = pack.variants.some(
        (v) => v.available && v.options[index] === value && this.matches(v, index, value)
      );
      btn.classList.toggle('is-unavailable', !anyAvailable);
    });
  }

  update() {
    this.updatePackRows();
    this.updateChips();

    const pack = this.packs[this.packIndex];
    const variant = this.findVariant(this.packIndex);

    const input = this.querySelector('[data-funday-variant-id]');
    if (input && variant) input.value = variant.id;

    if (this.form) {
      const multi = pack.count > 1;
      this.form.classList.toggle('funday-pack-multi', multi);
      if (multi) this.clearSellingPlan();
    }

    const cta = this.querySelector('[data-funday-cta]');
    const ctaText = this.querySelector('[data-funday-cta-text]');

    if (!variant) {
      if (cta) cta.disabled = true;
      if (ctaText) ctaText.textContent = 'Unavailable';
    } else if (!variant.available) {
      if (cta) cta.disabled = true;
      if (ctaText) ctaText.textContent = 'Sold out';
    } else {
      if (cta) cta.disabled = false;
      const price = this.formatMoney(variant.price);
      if (ctaText) {
        ctaText.textContent = pack.count > 1 && pack.label
          ? `Add ${pack.label} — ${price}`
          : `Add to cart — ${price}`;
      }
    }

    if (variant) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    this.dispatchEvent(new CustomEvent('funday:pack-change', {
      bubbles: true,
      detail: { pack: pack.label, count: pack.count, variant }
    }));
  }
}

if (!customElements.get('funday-pack-picker')) {
  customElements.define('funday-pack-picker', FundayPackPicker);
}
